import Config from '../utils/config';

const ASSISTANT_TIMEOUT_MS = 5 * 60 * 1000;
const STREAM_STATUS_PREFIX = 'STATUS:';
const STREAM_MESSAGE_PREFIX = 'MESSAGE:';
const STREAM_TAG_PREFIXES = [STREAM_STATUS_PREFIX, STREAM_MESSAGE_PREFIX];
const STREAM_STATUS_NODE_MATCH = 'status agent';
const STREAM_MESSAGE_NODE_MATCHES = ['composer agent (final response)', 'composer agent'];

export interface AssistantRequest {
  message: string;
  sessionId?: string;
  accountId?: number;
  projectId?: number;
  context?: Record<string, unknown>;
}

export interface AssistantResponse {
  message?: string;
  error?: string;
  wasStreamed?: boolean;
  rawText?: string;
  [key: string]: any;
}

export interface AssistantStreamCallbacks {
  onStatus?: (status: string | null) => void;
  onMessage?: (message: string) => void;
  signal?: AbortSignal;
}

const resolveUserContext = () => {
  try {
    const stored = localStorage.getItem('quipa-store');
    if (!stored) return undefined;
    const parsed = JSON.parse(stored);
    const user = parsed?.user;
    if (!user) return undefined;
    return {
      id: user.id,
      email: user.email,
      name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || undefined,
    };
  } catch (_error) {
    return undefined;
  }
};

const makeSessionId = () => `assistant-${Date.now()}-${Math.round(Math.random() * 1e6)}`;

const resolveAccountId = (): number | undefined => {
  try {
    const rawStore = localStorage.getItem('quipa-store');
    const parsed = rawStore ? JSON.parse(rawStore) : null;
    const fromUser = parsed?.user?.accountId || parsed?.user?.account?.id;
    const stored = localStorage.getItem('accountId');
    const fromLocal = stored ? Number(stored) : undefined;
    const candidate = fromUser || fromLocal;
    return Number.isFinite(candidate as number) ? (candidate as number) : undefined;
  } catch (_err) {
    return undefined;
  }
};

const buildPayload = ({
  message,
  sessionId,
  accountId,
  projectId,
  context = {},
}: AssistantRequest) => {
  const user = resolveUserContext();
  const finalSessionId = sessionId || makeSessionId();
  const finalAccountId = accountId ?? resolveAccountId();

  return {
    message,
    sessionId: finalSessionId,
    session_id: finalSessionId,
    chatId: finalSessionId,
    accountId: finalAccountId,
    account_id: finalAccountId,
    projectId,
    user,
    source: 'quipa-frontend',
    context,
  };
};

const unwrapResponse = (res: any): AssistantResponse => {
  if (typeof res === 'string') return { message: res };
  if (!res) return {};
  if (typeof res.message === 'string') return { message: res.message, ...res };
  if (typeof res.text === 'string') return { message: res.text, ...res };
  if (typeof res.content === 'string') return { message: res.content, ...res };
  if (typeof res.reply === 'string') return { message: res.reply, ...res };
  if (typeof res.response === 'string') return { message: res.response, ...res };
  if (typeof res.output === 'string') return { message: res.output, ...res };
  if (res.output && typeof res.output.message === 'string') {
    return { message: res.output.message, ...res };
  }
  if (res.data && typeof res.data === 'object') return unwrapResponse(res.data);
  return res;
};

const isAbortError = (error: unknown) =>
  error instanceof DOMException
    ? error.name === 'AbortError'
    : !!(error && typeof error === 'object' && (error as any).name === 'AbortError');

/**
 * Sends a chat message to the n8n QUIPA Assistant webhook.
 * Supports both one-shot JSON replies and n8n streaming responses.
 */
const sendAssistantMessage = async ({
  message,
  sessionId,
  accountId,
  projectId,
  context = {},
}: AssistantRequest, callbacks: AssistantStreamCallbacks = {}): Promise<AssistantResponse> => {
  const payload = buildPayload({ message, sessionId, accountId, projectId, context });
  const controller = new AbortController();
  const forwardAbort = () => controller.abort();
  const timeoutId = window.setTimeout(() => controller.abort(), ASSISTANT_TIMEOUT_MS);

  if (callbacks.signal) {
    if (callbacks.signal.aborted) {
      controller.abort();
    } else {
      callbacks.signal.addEventListener('abort', forwardAbort);
    }
  }

  let rawResponseText = '';
  let sawStreamItems = false;
  let streamedText = '';
  let lastStatus: string | null = null;
  let lastMessage = '';

  const notifyStreamState = () => {
    const { status, message: nextMessage } = deriveTaggedAssistantState(streamedText);
    if (status !== lastStatus) {
      lastStatus = status;
      callbacks.onStatus?.(status);
    }

    if (nextMessage !== lastMessage) {
      lastMessage = nextMessage;
      if (nextMessage) callbacks.onMessage?.(nextMessage);
    }
  };

  try {
    const response = await fetch(Config.n8nWebhookUrl, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify(payload),
      signal: controller.signal,
    });

    if (response.body) {
      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let envelopeBuffer = '';

      while (true) {
        const { done, value } = await reader.read();
        const decoded = value ? decoder.decode(value, { stream: !done }) : '';
        if (decoded) {
          rawResponseText += decoded;
          envelopeBuffer += decoded;

          const { objects, remainder } = extractJsonObjects(envelopeBuffer);
          envelopeBuffer = remainder;

          if (objects.length > 0) {
            objects.forEach((entry) => {
              if (entry?.type === 'item' && typeof entry.content === 'string') {
                const nextText = extractStreamContent(entry);
                if (nextText) {
                  sawStreamItems = true;
                  streamedText += nextText;
                  notifyStreamState();
                }
              }
            });
          }
        }

        if (done) {
          const tail = decoder.decode();
          if (tail) rawResponseText += tail;
          break;
        }
      }
    } else {
      rawResponseText = await response.text();
    }

    if (!response.ok) {
      const errorBody = rawResponseText.trim();
      throw new Error(errorBody || `Assistant request failed with status ${response.status}`);
    }

    if (!sawStreamItems) {
      const aggregatedText = aggregateFromTextPayload(rawResponseText);
      if (aggregatedText) {
        streamedText = aggregatedText;
        notifyStreamState();
        sawStreamItems = true;
      }
    }

    if (sawStreamItems) {
      const finalState = deriveTaggedAssistantState(streamedText);
      callbacks.onStatus?.(finalState.message ? null : finalState.status);
      if (finalState.message) callbacks.onMessage?.(finalState.message);
      return {
        message: finalState.message || undefined,
        wasStreamed: true,
        rawText: streamedText,
      };
    }

    const parsed = safeParseJson(rawResponseText);
    return {
      ...unwrapResponse(parsed ?? rawResponseText),
      wasStreamed: false,
      rawText: rawResponseText,
    };
  } catch (error) {
    if (isAbortError(error)) {
      if (callbacks.signal?.aborted) throw error;
      throw new Error('Assistant request timed out.');
    }
    throw error;
  } finally {
    window.clearTimeout(timeoutId);
    if (callbacks.signal) {
      callbacks.signal.removeEventListener('abort', forwardAbort);
    }
  }
};

const safeParseJson = (line: string): any => {
  try {
    return JSON.parse(line);
  } catch (_err) {
    return null;
  }
};

const containsStreamTag = (value: string): boolean =>
  STREAM_TAG_PREFIXES.some((prefix) => value.includes(prefix));

const getStreamNodeName = (entry: any): string =>
  String(entry?.metadata?.nodeName ?? '').trim().toLowerCase();

const isStatusStreamNode = (entry: any): boolean =>
  getStreamNodeName(entry).includes(STREAM_STATUS_NODE_MATCH);

const isMessageStreamNode = (entry: any): boolean => {
  const nodeName = getStreamNodeName(entry);
  return STREAM_MESSAGE_NODE_MATCHES.some((match) => nodeName.includes(match));
};

const findTaggedText = (value: unknown, depth = 0): string => {
  if (depth > 4 || value == null) return '';

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (!trimmed) return '';
    if (containsStreamTag(trimmed)) return trimmed;

    const parsed = safeParseJson(trimmed);
    if (parsed != null && parsed !== value) {
      return findTaggedText(parsed, depth + 1);
    }

    return '';
  }

  if (Array.isArray(value)) {
    for (const item of value) {
      const match = findTaggedText(item, depth + 1);
      if (match) return match;
    }
    return '';
  }

  if (typeof value === 'object') {
    const obj = value as Record<string, unknown>;
    const preferredKeys = ['message', 'text', 'content', 'reply', 'response', 'output', 'data'];

    for (const key of preferredKeys) {
      if (key in obj) {
        const match = findTaggedText(obj[key], depth + 1);
        if (match) return match;
      }
    }

    for (const nestedValue of Object.values(obj)) {
      const match = findTaggedText(nestedValue, depth + 1);
      if (match) return match;
    }
  }

  return '';
};

const extractJsonObjects = (input: string): { objects: any[]; remainder: string } => {
  const objects: any[] = [];
  let start = -1;
  let depth = 0;
  let inString = false;
  let escaping = false;

  for (let i = 0; i < input.length; i += 1) {
    const char = input[i];

    if (start === -1) {
      if (char === '{') {
        start = i;
        depth = 1;
        inString = false;
        escaping = false;
      }
      continue;
    }

    if (inString) {
      if (escaping) {
        escaping = false;
      } else if (char === '\\') {
        escaping = true;
      } else if (char === '"') {
        inString = false;
      }
      continue;
    }

    if (char === '"') {
      inString = true;
      continue;
    }

    if (char === '{') {
      depth += 1;
      continue;
    }

    if (char === '}') {
      depth -= 1;
      if (depth === 0) {
        const candidate = input.slice(start, i + 1);
        const parsed = safeParseJson(candidate);
        if (parsed != null) objects.push(parsed);
        start = -1;
      }
    }
  }

  return {
    objects,
    remainder: start === -1 ? '' : input.slice(start),
  };
};

const extractStreamContent = (entry: any): string => {
  if (!entry || entry.type !== 'item' || typeof entry.content !== 'string') return '';

  if (isStatusStreamNode(entry) || isMessageStreamNode(entry)) {
    return entry.content;
  }

  const { content } = entry;
  const directMatch = findTaggedText(content);
  if (directMatch) return directMatch;

  const parsed = safeParseJson(content);
  if (parsed != null) {
    const nestedMatch = findTaggedText(parsed);
    if (nestedMatch) return nestedMatch;
  }

  return '';
};

const deriveTaggedAssistantState = (rawText: string): { status: string | null; message: string } => {
  const text = rawText.replace(/\r\n/g, '\n');
  const messageIndex = text.indexOf(STREAM_MESSAGE_PREFIX);
  const statusRegion = messageIndex >= 0 ? text.slice(0, messageIndex) : text;
  const statusSegments = statusRegion.split(STREAM_STATUS_PREFIX).slice(1);
  const status = statusSegments
    .map((segment) => segment.split('\n')[0].trim())
    .filter(Boolean)
    .pop() || null;

  if (messageIndex >= 0) {
    return {
      status: null,
      message: text.slice(messageIndex + STREAM_MESSAGE_PREFIX.length).replace(/^\s*/, ''),
    };
  }

  if (statusSegments.length > 0) {
    return { status, message: '' };
  }

  return { status: null, message: '' };
};

const aggregateFromTextPayload = (text: string): string => {
  if (!text) return '';
  const lines = text.split('\n').filter((l) => l.trim());
  let aggregated = '';
  for (const line of lines) {
    const parsed = safeParseJson(line);
    if (parsed?.type === 'item' && typeof parsed.content === 'string') {
      aggregated += extractStreamContent(parsed);
    }
  }
  return aggregated;
};

export { sendAssistantMessage };
